import {
  DestroyRef,
  effect,
  inject,
  Injectable,
  InjectionToken,
  Injector,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, fromEvent, Observable, of, Subscription, switchMap, take, timer } from 'rxjs';
import { parseToken } from 'utilities';
import { HostMessage, HostMessageType } from './interfaces/host-message-types';
import {
  HostMessagePayload,
  TokenSetupPayload,
  ExternalCommandPayload,
} from './interfaces/host-message-payloads.types';
import { MessageHandler } from './messages/base/interfaces/message.interface';
import { AddNewModelMessage } from './messages/add-new-model/add-new-model.message';
import { ModelUploadMessage } from './messages/model-upload/model-upload.message';
import { ModelDeletedMessage } from './messages/model-deleted/model-deleted.message';
import { ViewerReadyMessage } from './messages/viewer-ready/viewer-ready.message';
import { ViewerErrorMessage } from './messages/viewer-error/viewer-error.message';
import { AuthenticatedMessage } from './messages/authenticated/authenticated.message';
import { AuthenticationFailedMessage } from './messages/authentication-failed/authentication-failed.message';
import { AuthStoreService } from '../../../data-access/auth/auth-store.service';
import { normalizeAppReturnUrl } from '../../../data-access/auth/auth-redirect.service';
import {
  HostTimeoutHandler,
  createPersistentAwaitingTokenHandler,
} from './handlers/host-timeout/host-timeout-handler';
import { sendMessageToHost } from './messages/base/models/base-message';
import { MessagesStream } from './messages/messages-stream';
import { UrlFlagsService } from '../url-flags/url-flags.service';
import { TranslateService } from '@ngx-translate/core';
import { toast } from 'ngx-sonner';
import { ROUTE } from '../../constants';
import { LoginResponse } from 'angular-auth-oidc-client';
import { EncryptedStorageService } from '../..';

import { AwaitingSceneMessage } from './messages/awaiting-scene/awaiting-scene.message';
import { ModelUploadCommandMessage } from './messages/model-upload-command/model-upload-command.message';
import type { ModelUploadCommandPayload } from './messages/model-upload-command/interfaces/model-upload-command-payload.interface';
import { AwaitingUploadSetupMessage } from './messages/awaiting-upload-setup/awaiting-upload-setup.message';
import { InitializedMessage } from './messages/initialized/initialized.message';
import { OpenedPageMessage } from './messages/opened-page/opened-page.message';
import { ClosedMessage } from './messages/closed/closed.message';
import { CancelModelUploadMessage } from './messages/cancel-model-upload/cancel-model-upload.message';
import type { CancelModelUploadPayload } from './messages/cancel-model-upload/interfaces/cancel-model-upload-payload.interface';
import { CancelModelSetupMessage } from './messages/cancel-model-setup/cancel-model-setup.message';
import type { CancelModelSetupPayload } from './messages/cancel-model-setup/interfaces/cancel-model-setup-payload.interface';
import { TaskSelectedMessage } from './messages/task-selected/task-selected.message';
import type { TaskSelectedPayload } from './messages/task-selected/interfaces/task-selected-payload.interface';
import { TasksCheckedMessage } from './messages/tasks-checked/tasks-checked.message';
import type { TasksCheckedPayload } from './messages/tasks-checked/interfaces/tasks-checked-payload.interface';
import { CreateTaskMessage } from './messages/create-task/create-task.message';
import type { CreateTaskMessagePayload } from './messages/create-task/interfaces/create-task-payload.interface';
import { RequestLogoutMessage } from './messages/request-logout/request-logout.message';
import { EventBusService } from '../event-bus/event-bus.service';
import { UserMapper } from '@web-portal/domains/user/anti-corruption-layer';
import { UserEventBus } from '@web-portal/domains/shared/event-bus';
import { Handler } from './interfaces/handler.interface';
import { HostHandler } from './handlers/host/host.handler';
import { TokenOriginHandler } from './handlers/host/token-origin.handler';
import { HostCommunicationStore } from './host-communication.store';
import { UploadStatusRuntimeStoreService } from '../upload-status/upload-status-runtime-store.service';
import type { ModelUploadStatusPayload } from './messages/model-upload-status/interfaces/model-upload-status-payload.interface';
import type { SceneSetupPayload } from './messages/scene-setup/interfaces/scene-setup-payload.interface';
import { BIMPLUS_API } from '@web-portal/domains/shared/bimplus-core';
import type { Api } from 'bimplus-websdk';
declare global {
  interface Window {
    chrome?: {
      webview?: {
        postMessage: (message: unknown) => void;
      };
    };
  }
}

/** Seconds before token expiry to trigger a refresh (buffer). */
const TOKEN_REFRESH_BUFFER_S = 60;

/**
 * Origins of iframe hosts allowed to message the app. When provided, iframe messages must also come
 * from the parent window; an empty list rejects them all. Not provided means unrestricted.
 */
export const HOST_MESSAGING_TRUSTED_ORIGINS = new InjectionToken<readonly string[]>(
  'HOST_MESSAGING_TRUSTED_ORIGINS'
);

/**
 * Centralized service for host communication in embedded mode.
 *
 * Provides typed methods for sending messages to the parent window
 * and receiving messages from the host. Supports timeout/retry patterns
 * for operations like authentication token handshake.
 */
@Injectable({
  providedIn: 'root',
})
export class HostCommunicationService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly authStoreService = inject(AuthStoreService);
  private readonly router = inject(Router);
  private readonly urlFlagsService = inject(UrlFlagsService);
  private readonly injector = inject(Injector);
  private readonly translateService = inject(TranslateService);
  private readonly userEventBus = inject(UserEventBus);
  private readonly eventBusService = inject(EventBusService);
  private readonly route = inject(ActivatedRoute);
  private readonly encryptedStorageService = inject(EncryptedStorageService);
  private readonly hostCommunicationStore = inject(HostCommunicationStore);
  private readonly uploadStatusStore = inject(UploadStatusRuntimeStoreService);
  private readonly bimplusApi = inject<Api | null>(BIMPLUS_API, { optional: true });

  private readonly messagesStream = new MessagesStream({
    trustedOrigins: inject(HOST_MESSAGING_TRUSTED_ORIGINS, { optional: true }) ?? undefined,
  });

  /** Active timeout handler for authentication. */
  private authTimeoutHandler: HostTimeoutHandler<string> | null = null;

  /** Subscription for the scheduled token refresh timer. */
  private tokenRefreshSub: Subscription | null = null;

  /** Callback for scene-setup message when awaiting scene update */
  private sceneSetupCallback: ((payload: SceneSetupPayload | undefined) => void) | null = null;

  private readonly hostSourceHandler: Handler<TokenSetupPayload> = new HostHandler();

  /** Map of message type → validated message handler. */
  private readonly messageHandlers = new Map<HostMessageType, MessageHandler>([
    [HostMessageType.AddNewModel, AddNewModelMessage],
    [HostMessageType.ModelUploaded, ModelUploadMessage],
    [HostMessageType.ModelDeleted, ModelDeletedMessage],
    [HostMessageType.ViewerReady, ViewerReadyMessage],
    [HostMessageType.ViewerError, ViewerErrorMessage],
    [HostMessageType.Authenticated, AuthenticatedMessage],
    [HostMessageType.AuthenticationFailed, AuthenticationFailedMessage],
    [HostMessageType.AwaitingUploadSetup, AwaitingUploadSetupMessage],
    [HostMessageType.AwaitingScene, AwaitingSceneMessage],
    [HostMessageType.ModelUpload, ModelUploadCommandMessage],
    [HostMessageType.Initialized, InitializedMessage],
    [HostMessageType.OpenedPage, OpenedPageMessage],
    [HostMessageType.Closed, ClosedMessage],
    [HostMessageType.CancelModelUpload, CancelModelUploadMessage],
    [HostMessageType.CancelModelSetup, CancelModelSetupMessage],
    [HostMessageType.TaskSelected, TaskSelectedMessage],
    [HostMessageType.TasksChecked, TasksCheckedMessage],
    [HostMessageType.CreateTask, CreateTaskMessage],
    [HostMessageType.RequestLogout, RequestLogoutMessage],
  ]);

  private _initialized = false;

  get isInitialized(): boolean {
    return this._initialized;
  }

  constructor() {
    this.hostSourceHandler.setNext(new TokenOriginHandler());
    // Auto-initialize when embedded() becomes true, including after an OIDC redirect
    // where the callback URL lacks ?embedded=1 but the destination URL has it.
    effect(() => {
      if (this.urlFlagsService.embedded()) {
        untracked(() => this.initialize());
      }
    });
  }

  initialize(): void {
    if (this._initialized) {
      return;
    }
    this._initialized = true;
    this.listenForAuthStateChanges();
    this.listenForHostMessages();
    this.listenForImportProgress();
    this.listenForNavigationChanges();
    this.listenForBeforeUnload();
    this.reportInitializedWhenEmbedded();
    this.eventBusService
      .on$<{ expires_in: number }>('tokenValidated')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(token => {
        this.scheduleTokenRefresh(token.expires_in);
      });
    this.eventBusService
      .on$('requestAuthentication')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.requestAuthentication();
      });
  }

  // ── Sending messages ────────────────────────────────────────

  /** Validates and sends a typed message to the host. */
  private send(messageType: HostMessageType, payload: unknown, targetOrigin = '*'): void {
    const handler = this.messageHandlers.get(messageType);
    if (handler?.validatePayload(payload)) {
      handler.send(payload, targetOrigin);
    }
  }

  reportAddNewModel(teamId: string, projectId: string, name: string, targetOrigin = '*'): void {
    this.send(HostMessageType.AddNewModel, { teamId, projectId, name }, targetOrigin);
  }

  reportModelUploaded(modelId: string, projectId: string, name: string, targetOrigin = '*'): void {
    this.send(HostMessageType.ModelUploaded, { modelId, projectId, name }, targetOrigin);
  }

  reportModelDeleted(modelId: string, targetOrigin = '*'): void {
    this.send(HostMessageType.ModelDeleted, { modelId }, targetOrigin);
  }

  reportViewerReady(viewerId: string, capabilities: string[], targetOrigin = '*'): void {
    this.send(HostMessageType.ViewerReady, { viewerId, capabilities }, targetOrigin);
  }

  reportViewerError(viewerId: string, error: string, code: string, targetOrigin = '*'): void {
    this.send(HostMessageType.ViewerError, { viewerId, error, code }, targetOrigin);
  }

  reportAuthenticated(targetOrigin = '*'): void {
    this.send(HostMessageType.Authenticated, undefined, targetOrigin);
  }

  reportAuthenticationFailed(reason: string, code: string, targetOrigin = '*'): void {
    this.send(HostMessageType.AuthenticationFailed, { reason, code }, targetOrigin);
  }

  awaitingUploadSetup(targetOrigin = '*'): void {
    this.send(HostMessageType.AwaitingUploadSetup, undefined, targetOrigin);
  }

  modelUpload(payload: ModelUploadCommandPayload | undefined, targetOrigin = '*'): void {
    this.send(HostMessageType.ModelUpload, payload, targetOrigin);
  }

  awaitingScene(targetOrigin = '*'): void {
    this.send(HostMessageType.AwaitingScene, undefined, targetOrigin);
  }

  reportInitialized(targetOrigin = '*'): void {
    this.send(HostMessageType.Initialized, undefined, targetOrigin);
  }

  /** Reports initialized as soon as embedded() becomes true (handles OIDC redirect case). */
  private reportInitializedWhenEmbedded(): void {
    toObservable(this.urlFlagsService.embedded, { injector: this.injector })
      .pipe(
        filter(embedded => embedded),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.reportInitialized());
  }

  reportOpenedPage(page: string, targetOrigin = '*'): void {
    this.send(HostMessageType.OpenedPage, { page }, targetOrigin);
  }

  reportClosed(targetOrigin = '*'): void {
    this.send(HostMessageType.Closed, undefined, targetOrigin);
  }

  cancelModelUpload(payload: CancelModelUploadPayload, targetOrigin = '*'): void {
    this.send(HostMessageType.CancelModelUpload, payload, targetOrigin);
  }

  cancelModelSetup(payload: CancelModelSetupPayload, targetOrigin = '*'): void {
    this.send(HostMessageType.CancelModelSetup, payload, targetOrigin);
  }

  reportTaskSelected(payload: TaskSelectedPayload, targetOrigin = '*'): void {
    this.send(HostMessageType.TaskSelected, payload, targetOrigin);
  }

  reportTasksChecked(payload: TasksCheckedPayload, targetOrigin = '*'): void {
    this.send(HostMessageType.TasksChecked, payload, targetOrigin);
  }

  reportCreateTask(payload: CreateTaskMessagePayload, targetOrigin = '*'): void {
    this.send(HostMessageType.CreateTask, payload, targetOrigin);
  }

  requestLogout(targetOrigin = '*'): void {
    this.send(HostMessageType.RequestLogout, undefined, targetOrigin);
  }

  /**
   * Registers a one-shot callback for the next scene-setup message.
   * The callback is automatically cleared after being invoked.
   */
  onNextSceneSetup(callback: (payload: SceneSetupPayload | undefined) => void): void {
    this.sceneSetupCallback = callback;
  }

  /**
   * Clears any pending scene-setup callback (e.g., when leaving the page).
   */
  clearSceneSetupCallback(): void {
    this.sceneSetupCallback = null;
  }

  // ── Receiving messages ──────────────────────────────────────

  onMessage(): Observable<HostMessage> {
    return this.messagesStream.incomingMessages$;
  }

  onMessageType<T extends HostMessagePayload>(type: HostMessageType): Observable<HostMessage<T>> {
    return this.messagesStream.incomingMessages$.pipe(
      filter((msg): msg is HostMessage<T> => msg.type === type)
    );
  }

  onExternalCommand(): Observable<ExternalCommandPayload> {
    return this.messagesStream.onExternalCommand();
  }

  onTokenSetup(): Observable<TokenSetupPayload> {
    return this.messagesStream.onTokenSetup();
  }

  // ── Authentication with timeout / retry ─────────────────────

  /**
   * Requests authentication token from the host.
   * Sends `awaiting-token` repeatedly until the host responds or the request is cancelled.
   */
  requestAuthentication(targetOrigin = '*') {
    this.cancelAuthenticationRequest();

    this.authTimeoutHandler = createPersistentAwaitingTokenHandler();

    this.authTimeoutHandler
      .start(() => {
        toast.info(this.translateService.instant('auth.authorization-pending') + '...', {
          description: this.translateService.instant('auth.authorization-pending-please-wait'),
          duration: 10000,
        });
        sendMessageToHost(HostMessageType.AwaitingToken, undefined, targetOrigin);
      })
      .subscribe();
  }

  cancelAuthenticationRequest(): void {
    if (this.authTimeoutHandler?.isPending()) {
      this.authTimeoutHandler.cancel();
    }
    this.authTimeoutHandler = null;
  }

  completeAuthentication(token: string): void {
    this.authTimeoutHandler?.complete(token);
  }

  handleHostSource(payload: TokenSetupPayload): void {
    const mergedPayload: TokenSetupPayload = {
      ...payload,
      host: payload.host ?? this.resolveHostFromQueryParams(),
    };
    const source = this.hostSourceHandler.handle(mergedPayload);
    this.hostCommunicationStore.setHostSource(source);
  }

  private resolveHostFromQueryParams(): string | undefined {
    const queryParams = this.route.snapshot.queryParams;
    if (queryParams['host']) {
      return queryParams['host'];
    }
    const returnUrl = queryParams['returnUrl'];
    if (!returnUrl) return undefined;
    try {
      const parsed = new URL(returnUrl, globalThis.location.origin);
      return parsed.searchParams.get('host') ?? undefined;
    } catch {
      return undefined;
    }
  }

  // ── Private: listeners ──────────────────────────────────────

  /**
   * Reacts to auth-store state:
   *  • reports `authenticated` to the host whenever a valid token is set
   *  • starts an authentication request when the app becomes unauthenticated
   */
  private listenForAuthStateChanges(): void {
    const isTokenInUrl = this.urlFlagsService.getFlag('token');

    this.authStoreService.accessToken$
      .pipe(
        filter(accessToken => !!accessToken),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.reportAuthenticated();
      });

    this.authStoreService.isAuthenticated$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(result => {
          if (result.isAuthenticated || isTokenInUrl) {
            this.cancelAuthenticationRequest();
            return of(true);
          }
          return of(!!this.authTimeoutHandler);
        })
      )
      .subscribe(handled => {
        if (!handled) {
          this.requestAuthentication();
        }
      });
  }

  /**
   * Sets up listeners for incoming host messages
   * and delegates token-setup handling to a dedicated method.
   */
  private listenForHostMessages(): void {
    this.messagesStream
      .onExternalCommand()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(payload => {
        console.log('Host sent command:', payload.command, payload.args);

        // Emit command event for other services to handle
        this.eventBusService.emit({
          type: '[Host] External command received',
          payload: {
            command: payload.command,
            args: payload.args ?? [],
          },
        });
      });

    this.messagesStream
      .onTokenSetup()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(payload => this.handleTokenSetup(payload));

    this.onMessageType<ModelUploadStatusPayload>(HostMessageType.ModelUploadStatus)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(msg => {
        if (msg.payload) {
          this.uploadStatusStore.processStatusUpdate(msg.payload);
        }
      });

    this.onMessageType<SceneSetupPayload>(HostMessageType.SceneSetup)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(msg => {
        if (this.sceneSetupCallback) {
          const callback = this.sceneSetupCallback;
          this.sceneSetupCallback = null; // Clear before calling (one-shot)
          callback(msg.payload);
        }
      });
  }

  /**
   * Listens for SignalR import-progress events (the same `'[User] import-progress'` event
   * used by the normal-mode models table) and applies live percentage updates to any
   * tracked upload-status item whose modelId matches the event's divisionid.
   */
  private listenForImportProgress(): void {
    this.userEventBus
      .on$<{ projectid: string; divisionid: string; state: string; percentage: number }>(
        '[User] import-progress'
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        if (
          event.state === 'PROCESSING' &&
          event.divisionid !== undefined &&
          event.percentage !== undefined
        ) {
          this.uploadStatusStore.applySignalrProgress(event.divisionid, event.percentage);
        }
      });
  }

  /**
   * Validates the incoming token by loading bimplus user info.
   * On success, updates auth state in place without route redirection.
   */
  private handleTokenSetup(payload: TokenSetupPayload): void {
    const token = payload.token;
    if (!token) {
      console.warn('Received TokenSetup message without token');
      return;
    }

    if (!this.bimplusApi) {
      AuthenticationFailedMessage.send({
        reason: 'Token validation is unavailable',
        code: 'TOKEN_VALIDATION_UNAVAILABLE',
      });
      return;
    }

    this.bimplusApi.user
      .get(token)
      .then(user => {
        const userId = user?.id ?? '';
        if (!userId) {
          throw new Error('Missing user id in token validation response');
        }

        this.completeAuthentication(token);
        const parsedToken = parseToken(token);
        this.encryptedStorageService
          .initializeStorageForUser({
            isAuthenticated: true,
            userData: {
              email: parsedToken?.['email'],
              sub: parsedToken?.['sub'],
            },
            accessToken: token,
            idToken: '',
          } satisfies LoginResponse)
          .catch(err => {
            console.error('Failed to initialize encrypted storage:', err);
            // Continue with authentication - storage failure shouldn't block the flow
          });

        this.handleHostSource(payload);
        this.authStoreService.updateAccessToken(token);
        this.authStoreService.setAuthenticated({
          isAuthenticated: true,
          allConfigsAuthenticated: [],
        });
        this.authStoreService.updateBimplusUserId(userId);
        this.updateUserData(token);
        this.navigateFromLoginToReturnUrlIfNeeded();
        this.scheduleTokenRefresh(this.resolveTokenTtlSeconds(token));
      })
      .catch(err => {
        console.error('Token validation failed:', err);
        AuthenticationFailedMessage.send({
          reason: err?.message ?? 'Token validation failed',
          code: err?.code ?? 'TOKEN_VALIDATION_ERROR',
        });
      });
  }

  /**
   * Schedules a proactive token refresh `TOKEN_REFRESH_BUFFER_S` before expiration.
   * Uses RxJS `timer` + `takeUntilDestroyed` so the subscription is
   * automatically cleaned up when the service is destroyed.
   */
  private scheduleTokenRefresh(expiresInSeconds: number): void {
    this.tokenRefreshSub?.unsubscribe();

    const refreshInMs = Math.max((expiresInSeconds - TOKEN_REFRESH_BUFFER_S) * 1000, 0);

    this.tokenRefreshSub = timer(refreshInMs)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.requestAuthentication();
      });
  }

  private listenForNavigationChanges(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(event => {
        this.reportOpenedPage(event.urlAfterRedirects);
      });
  }

  private listenForBeforeUnload(): void {
    fromEvent(globalThis, 'beforeunload')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.reportClosed();
      });
  }

  private updateUserData(token: string): void {
    this.userEventBus.emit({
      type: '[User] initialized',
      payload: UserMapper.mapToUser(parseToken(token)),
    });
  }

  private resolveTokenTtlSeconds(token: string): number {
    const parsedToken = parseToken(token);
    const expValue = parsedToken?.['exp'];
    const exp = typeof expValue === 'number' ? expValue : Number(expValue);
    if (!Number.isFinite(exp) || exp <= 0) {
      return TOKEN_REFRESH_BUFFER_S;
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);
    return Math.max(exp - nowInSeconds, TOKEN_REFRESH_BUFFER_S);
  }

  private navigateFromLoginToReturnUrlIfNeeded(): void {
    const currentUrl = this.router.url ?? '';
    const onLoginPage = currentUrl.startsWith(`/${ROUTE.login}`);
    if (!onLoginPage) {
      return;
    }

    const targetUrl = this.getLoginNavigationTarget(currentUrl);
    this.router.navigateByUrl(normalizeAppReturnUrl(targetUrl) ?? '/');
  }

  private getLoginNavigationTarget(currentUrl: string): string {
    try {
      const parsed = new URL(currentUrl, globalThis.location.origin);
      const fromCurrentUrl = parsed.searchParams.get('returnUrl');
      if (fromCurrentUrl) {
        return fromCurrentUrl;
      }

      const redirectTo = parsed.searchParams.get('redirectTo');
      if (redirectTo) {
        return redirectTo;
      }
    } catch {
      // Fallback to route snapshot below.
    }

    const fromSnapshot =
      this.route.snapshot.queryParams['returnUrl'] ?? this.route.snapshot.queryParams['redirectTo'];

    return fromSnapshot ?? '/';
  }

  private async populateBimplusUserId(token: string): Promise<void> {
    if (!this.bimplusApi || !token) {
      return;
    }

    try {
      const user = await this.bimplusApi.user.get(token);
      this.authStoreService.updateBimplusUserId(user?.id ?? '');
    } catch {
      this.authStoreService.updateBimplusUserId('');
    }
  }
}
